import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { ToastProvider } from './contexts/ToastContext';
import Toast from './components/ui/Toast';
import { GlobalLoader, VersionWatcher } from './components/shared';
import AuthProvider from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { PermissionRoute } from './components/shared/access';
import useFacebookPixel from './hooks/useFacebookPixel';
import usePublicPageViews from './hooks/usePublicPageViews';
import useVisualViewportHeight from './hooks/useVisualViewportHeight';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import NotFoundPage from './pages/NotFoundPage';
import EmailLinkAuthPage from './pages/EmailLinkAuthPage';
import AuthActionPage from './pages/AuthActionPage';
import AuthRedirect from './components/auth/AuthRedirect';
import ClinicTypeLandingPage from './pages/landing/ClinicTypeLandingPage';
import ClinicManagementPage from './pages/landing/ClinicManagementPage';
import StartPage from './pages/start/StartPage';
import ArticlesPage from './pages/landing/ArticlesPage';
import ArticlePage from './pages/landing/ArticlePage';

// Public Pages
import ConfirmAppointmentPage from './pages/public/ConfirmAppointmentPage';
import MeetPage from './pages/public/MeetPage';
import SignFormPage from './pages/public/SignFormPage';
import QuestionnaireResponsePage from './pages/public/QuestionnaireResponsePage';
import MyMenuPage from './pages/public/MyMenuPage';
import BookingPage from './pages/public/BookingPage';
import GrowPayPage from './pages/public/GrowPayPage';
import GrowThankYouPage from './pages/public/GrowThankYouPage';
import GrowPaymentTermsPage from './pages/public/GrowPaymentTermsPage';
import PoweredByCliniKit from './components/public/PoweredByCliniKit';

// Dashboard
import DashboardLayout from './components/dashboard/DashboardLayout';
import HomePage from './pages/dashboard/HomePage';
import PatientsPage from './pages/dashboard/PatientsPage';
import CreatePatientPage from './pages/dashboard/CreatePatientPage';
import PatientProfilePage from './pages/dashboard/PatientProfilePage';
import TreatmentDocPage from './pages/dashboard/TreatmentDocPage';
import TreatmentExportPage from './pages/dashboard/TreatmentExportPage';
import AiSummaryPage from './pages/dashboard/AiSummaryPage';
import LiveSessionPage from './pages/dashboard/LiveSessionPage';
import VideoMeetingPage from './pages/dashboard/VideoMeetingPage';
import CalendarPage from './pages/dashboard/CalendarPage';
import ClinicCalendarPage from './pages/dashboard/ClinicCalendarPage';
import FinancePage from './pages/dashboard/FinancePage';
import MonthlyReportPage from './pages/dashboard/MonthlyReportPage';
import MonthlyReportsPage from './pages/dashboard/MonthlyReportsPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import TasksRemindersPage from './pages/dashboard/TasksRemindersPage';
import FormEditorPage from './pages/dashboard/FormEditorPage';
import FoodsPage from './pages/dashboard/nutrition/FoodsPage';
import MenuBuilderPage from './pages/dashboard/nutrition/MenuBuilderPage';
import NutritionWorkspacePage from './pages/dashboard/nutrition/NutritionWorkspacePage';

// Admin Pages
import AdminOverviewPage from './pages/dashboard/admin/AdminOverviewPage';
import AdminPatientsPage from './pages/dashboard/admin/AdminPatientsPage';
import AdminCalendarPage from './pages/dashboard/admin/AdminCalendarPage';
import AdminTasksPage from './pages/dashboard/admin/AdminTasksPage';
import AdminActivityLogsPage from './pages/dashboard/admin/AdminActivityLogsPage';
import ClinicSettingsPage from './pages/dashboard/admin/ClinicSettingsPage';
import ClinicSnapshotPage from './pages/dashboard/admin/ClinicSnapshotPage';
import TeamPage from './pages/dashboard/admin/TeamPage';

// NiV Dashboard Pages (SaaS Admin)
import NivDashboardPage from './pages/dashboard/niv/NivDashboardPage';
import NivClinicDetailPage from './pages/dashboard/niv/clinic/NivClinicDetailPage';
import NivLeadPage from './pages/dashboard/niv/lead/NivLeadPage';

// Support Center
import SupportCenterPage from './pages/dashboard/SupportCenterPage';

// Legal Pages
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccessibilityStatementPage from './pages/AccessibilityStatementPage';
import AccessibilityWidget from './components/accessibility/AccessibilityWidget';

function FacebookPixelTracker() {
  useFacebookPixel();
  return null;
}

// First-party page views for the public marketing pages (NiV "שימוש ומדדים").
function PublicPageViewTracker() {
  usePublicPageViews();
  return null;
}

// Keeps --vvh (real visible viewport height) on <html> for mobile modals.
function VisualViewportTracker() {
  useVisualViewportHeight();
  return null;
}

// /dashboard/niv2/:tab → /dashboard/niv/:tab (old bookmarks)
function NivLegacyRedirect() {
  const { tab } = useParams();
  return <Navigate to={`/dashboard/niv/${tab || ''}`} replace />;
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <ToastProvider>
          <UnsavedChangesProvider>
            <AuthProvider>
              <FacebookPixelTracker />
              <PublicPageViewTracker />
              <VisualViewportTracker />
              <VersionWatcher />
              <GlobalLoader />
              <Toast />
              <AccessibilityWidget />
              <Routes>
            {/* Public Routes — redirect authenticated users to dashboard */}
            <Route path="/" element={<AuthRedirect><LandingPage /></AuthRedirect>} />
            <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
            <Route path="/signup" element={<AuthRedirect><SignupPage /></AuthRedirect>} />
            <Route path="/auth/email-callback" element={<EmailLinkAuthPage />} />
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route path="/confirm/:linkId" element={<><ConfirmAppointmentPage /><PoweredByCliniKit source="confirm_page" /></>} />
            {/* Grow payments (grow_integration.md Phase 6 + grow_part2.md Phase 2): /pay/terms (prerendered), /pay/:code interstitial with terms consent → Grow page, thank-you after payment. */}
            <Route path="/pay/terms" element={<GrowPaymentTermsPage />} />
            <Route path="/pay/thanks" element={<><GrowThankYouPage /><PoweredByCliniKit source="grow_thanks_page" /></>} />
            <Route path="/pay/:code" element={<GrowPayPage />} />
            <Route path="/meet/:linkId" element={<><MeetPage /><PoweredByCliniKit source="meet_page" /></>} />
            <Route path="/sign/:linkId" element={<><SignFormPage /><PoweredByCliniKit source="sign_page" /></>} />
            <Route path="/questionnaire/:linkId" element={<><QuestionnaireResponsePage /><PoweredByCliniKit source="questionnaire_page" /></>} />
            {/* Nutrition clinics — the client's personal page. Deliberately NOT
                added to scripts/prerender.js: it is private, per-client content. */}
            <Route path="/menu/:linkId" element={<MyMenuPage />} />
            <Route path="/book/:slug" element={<><BookingPage /><PoweredByCliniKit source="booking_page" /></>} />
            <Route path="/for/clinic-management" element={<ClinicManagementPage />} />
            <Route path="/for/:slug" element={<ClinicTypeLandingPage />} />
            {/* Lead-gen page — intentionally NOT wrapped in AuthRedirect (plan §0) */}
            <Route path="/start" element={<StartPage />} />
            <Route path="/blog" element={<ArticlesPage />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/accessibility" element={<AccessibilityStatementPage />} />

            {/* Protected Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* NiV Dashboard Routes (SaaS Admin) — single dashboard, URL-addressable tabs.
                  Legacy /niv/tasks and /niv/support resolve to tabs inside the page;
                  legacy /niv2/* redirects here. */}
              <Route path="niv" element={<NivDashboardPage />} />
              <Route path="niv/clinic/:clinicId" element={<NivClinicDetailPage />} />
              <Route path="niv/lead/:phoneKey" element={<NivLeadPage />} />
              <Route path="niv/:tab" element={<NivDashboardPage />} />
              <Route path="niv/:tab/:sub" element={<NivDashboardPage />} />
              <Route path="niv2" element={<Navigate to="/dashboard/niv" replace />} />
              <Route path="niv2/:tab" element={<NivLegacyRedirect />} />
              
              <Route index element={<HomePage />} />
              <Route path="patients" element={<PatientsPage />} />
              <Route path="patients/new" element={<CreatePatientPage />} />
              {/* Multi-clinic v2 — non-admin clinic-wide patients list (permission-gated).
                  Must stay BEFORE patients/:patientId so "all" is not captured as an id. */}
              <Route path="patients/all" element={<PermissionRoute perm="patients.viewAllClinic"><AdminPatientsPage /></PermissionRoute>} />
              <Route path="patients/:patientId" element={<PatientProfilePage />} />
              <Route path="patients/:patientId/treatments/:treatmentId" element={<TreatmentDocPage />} />
              <Route path="patients/:patientId/treatments/:treatmentId/export" element={<TreatmentExportPage />} />
              <Route path="patients/:patientId/ai-summary" element={<AiSummaryPage />} />
              {/* Nutrition clinics only — the page itself is gated on selectIsNutritionClinic */}
              <Route path="patients/:patientId/menu" element={<MenuBuilderPage />} />
              <Route path="patients/:patientId/menu/:menuId" element={<MenuBuilderPage />} />
              <Route path="live-session/:appointmentId" element={<LiveSessionPage />} />
              <Route path="video-meeting/:appointmentId" element={<VideoMeetingPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              {/* Multi-clinic v2 — clinic calendar (read-only/managed per permission; page gates itself) */}
              <Route path="clinic-calendar" element={<ClinicCalendarPage />} />
              <Route path="forms" element={<Navigate to="/dashboard/tasks?tab=forms" replace />} />
              <Route path="forms/new" element={<FormEditorPage />} />
              <Route path="forms/:formId" element={<FormEditorPage />} />
              <Route path="finance" element={<FinancePage />} />
              <Route path="finance/reports" element={<Navigate to="/dashboard/reports" replace />} />
              <Route path="reports" element={<MonthlyReportsPage />} />
              <Route path="reports/:monthKey" element={<MonthlyReportPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="tasks" element={<TasksRemindersPage />} />
              <Route path="support" element={<SupportCenterPage />} />

              {/* Nutrition clinics only — the pages themselves are gated on selectIsNutritionClinic */}
              <Route path="foods" element={<FoodsPage />} />
              <Route path="nutrition" element={<NutritionWorkspacePage />} />
              <Route path="nutrition/:patientId" element={<NutritionWorkspacePage />} />
              
              {/* Admin Routes */}
              <Route path="admin/snapshot" element={<AdminRoute><ClinicSnapshotPage /></AdminRoute>} />
              <Route path="admin/overview" element={<AdminRoute><AdminOverviewPage /></AdminRoute>} />
              <Route path="admin/patients" element={<AdminRoute><AdminPatientsPage /></AdminRoute>} />
              <Route path="admin/calendar" element={<AdminRoute><AdminCalendarPage /></AdminRoute>} />
              <Route path="admin/tasks" element={<AdminRoute><AdminTasksPage /></AdminRoute>} />
              <Route path="admin/activity-logs" element={<AdminRoute><AdminActivityLogsPage /></AdminRoute>} />
              <Route path="admin/settings" element={<AdminRoute><ClinicSettingsPage /></AdminRoute>} />
              <Route path="admin/team" element={<AdminRoute><TeamPage /></AdminRoute>} />

              {/* Multi-clinic v2 — non-admin: own activity rows (rules: therapistId == me) */}
              <Route path="activity" element={<AdminActivityLogsPage selfOnly />} />
              
              {/* 404 for unknown dashboard routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            
            {/* Global 404 */}
            <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AuthProvider>
          </UnsavedChangesProvider>
        </ToastProvider>
      </Router>
    </Provider>
  );
}

export default App;
